/**
 * stripePublishableKey.ts: which Stripe key may reach the browser, decided in
 * ONE place for both the build (vite.config.ts) and the page (web/stripeJs.ts).
 *
 * Tyler's ruling N1 (2026-09-29): the website's publishable key is baked into
 * the build from VITE_STRIPE_PUBLISHABLE_KEY, through vite.config.ts's existing
 * `define` block, the way mobile does it. Staging builds with the TEST key and
 * prod with the LIVE key; Nick adds the variable to each deployment.
 *
 * 🔴 ONLY A PUBLISHABLE KEY, EVER. A key that is not `pk_test_…` or `pk_live_…`
 * is refused here and the build ships NO key, loudly. That matters more than it
 * looks: Vite copies EVERY `VITE_`-prefixed variable in process.env into the
 * bundle's `import.meta.env` on its own, so a secret or restricted key pasted
 * into this variable by mistake would be published to every visitor. The
 * `define` entry is the override that stops it, and this is the check the
 * define is fed from.
 *
 * 🔑 NO FALLBACK TO ANOTHER VARIABLE. Mobile's dev script reads
 * STRIPE_PUBLISHABLE_KEY; this reads only VITE_STRIPE_PUBLISHABLE_KEY. A
 * fallback chain across names is how the browser's Sentry DSN ended up being
 * the server's (vite.config.ts, 2026-09-28): the build looked configured while
 * it carried somebody else's value.
 *
 * ⚠️ A MISSING KEY NEVER FAILS THE BUILD OR THE PAGE. It prints the warning
 * below (the same shape as #966's VITE_SENTRY_DSN warning) and the website's
 * $1 in-person fee stays disabled: the payer's button says card payment isn't
 * connected, and nothing is posted. Honest and dead, never a crash.
 *
 * Pure on purpose: no import.meta.env, no DOM, no process. The build runs it
 * in Node and the page runs it in the browser.
 */

/** The one variable the website's key comes from. */
export const STRIPE_KEY_ENV = "VITE_STRIPE_PUBLISHABLE_KEY";

/** A Stripe publishable key, test or live. Stripe keys are base62 after the prefix. */
export const PUBLISHABLE_KEY_PATTERN = /^pk_(test|live)_[A-Za-z0-9]+$/;

/** The key, trimmed, when it is a publishable key; null for anything else. */
export function publishableKeyOrNull(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const key = raw.trim();
  return PUBLISHABLE_KEY_PATTERN.test(key) ? key : null;
}

export const MISSING_KEY_WARNING =
  "\n  ⚠ VITE_STRIPE_PUBLISHABLE_KEY is not set: this build ships NO Stripe publishable key and\n" +
  "    the website's $1 in-person fee stays disabled (a payer's button says card payment isn't\n" +
  "    connected). Set it in the support build environment: the TEST key (pk_test_) on staging,\n" +
  "    the LIVE key (pk_live_) on prod.\n";

/** Never echoes the value: a value that fails this check may be a secret. */
export const NOT_PUBLISHABLE_WARNING =
  "\n  ⚠ VITE_STRIPE_PUBLISHABLE_KEY is set but is NOT a publishable key (pk_test_ or pk_live_):\n" +
  "    this build ships NO Stripe key and the website's $1 in-person fee stays disabled. A secret\n" +
  "    (sk_) or restricted (rk_) key must never go in a browser build; if one was put here, rotate it.\n";

export const LIVE_KEY_ON_STAGING_WARNING =
  "\n  ⚠ VITE_STRIPE_PUBLISHABLE_KEY is a LIVE key (pk_live_) on a build tagged SENTRY_ENVIRONMENT=staging.\n" +
  "    Staging's server holds TEST keys, so every $1 fee on this build will fail to confirm.\n" +
  "    Use the TEST key (pk_test_) on staging.\n";

/** What the build bakes, and what it has to say about it. */
export function stripeKeyForBuild(env: Readonly<Record<string, string | undefined>>): {
  key: string;
  warnings: string[];
} {
  const raw = (env[STRIPE_KEY_ENV] ?? "").trim();
  if (!raw) return { key: "", warnings: [MISSING_KEY_WARNING] };
  const key = publishableKeyOrNull(raw);
  if (!key) return { key: "", warnings: [NOT_PUBLISHABLE_WARNING] };
  const warnings: string[] = [];
  if (env.SENTRY_ENVIRONMENT === "staging" && key.startsWith("pk_live_")) {
    warnings.push(LIVE_KEY_ON_STAGING_WARNING);
  }
  return { key, warnings };
}

/**
 * The line vite.config.ts runs: the key to bake, with every warning printed
 * when this is a production build. The dev server stays quiet (a developer
 * without the key is normal there). Never throws.
 */
export function resolveBuildStripeKey(
  env: Readonly<Record<string, string | undefined>>,
  isBuild: boolean,
  warn: (message: string) => void = (m) => console.warn(m),
): string {
  const { key, warnings } = stripeKeyForBuild(env);
  if (isBuild) for (const w of warnings) warn(w);
  return key;
}
